'use client';

import { useState, useCallback } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import SliderControls from './SliderControls';
import ShellButton from './ShellButton';

interface ProcessedProduct {
  id: string;
  name: string;
  description: string;
  backgroundImage: string;
  productImage: string;
  isDarkTheme: boolean;
  category?: string;
  lowestPrice: number;
  isKgPrice: boolean;
  translatedSlug?: string;
  slug: string;
}

interface ProductSliderProps {
  products: ProcessedProduct[];
}

function formatPrice(price: number, currency: string = '€') {
  // Format to max 2 decimal places, removing trailing zeros
  const formatted = price.toFixed(2).replace(/\.?0+$/, '');
  return `${currency}${formatted}`;
}

export default function ProductSlider({ products }: ProductSliderProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const tProductSlider = useTranslations('productSlider');
  const { locale } = useParams();

  const handleSlideChange = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  return (
    <div className="relative h-full w-full" id="product-slider">
      {/* Products slider */}
      <div className="h-full w-full">
        {products.map((product, index) => (
          <div 
            key={product.id} 
            className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
            data-slide-index={index}
            style={{ 
              opacity: index === currentSlide ? 1 : 0,
              zIndex: index === currentSlide ? 10 : 5
            }}
          >
            {/* Background image - no blur on mobile, full blur on desktop */}
            <div className="absolute inset-0 z-0">
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute inset-0 md:backdrop-blur-[100px] transition-all duration-300">
                  <Image 
                    src={product.backgroundImage} 
                    alt={`${product.name}`}
                    fill
                    priority={index === 0}
                    className="object-cover object-center scale-125"
                    quality={90}
                  />
                </div>
              </div>
            </div>
            
            {/* Main content layout: text section on left, product space on right */}
            <div className="absolute inset-0 z-10 flex md:flex-row h-full" data-theme={product.isDarkTheme ? 'dark' : 'light'}>
              {/* Text section - only a contained box in the middle on mobile, full height on desktop */}
              <div className="relative w-full md:w-1/3 h-full flex items-center justify-center">
                {/* Contained box only on mobile, full height on desktop */}
                <div className="w-full md:h-full relative md:py-0 md:flex md:items-center">
                  {/* Backdrop blur ONLY for the contained text box on mobile */}
                  <div className={`absolute inset-0 md:inset-0 md:rounded-none backdrop-blur-xl bg-transparent md:border-r ${product.isDarkTheme ? 'border-black/10' : 'border-white/10'}`}>
                    {/* Semi-transparent gradient overlay for better text readability */}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/40 md:hidden"></div>
                  </div>

                  {/* Content container - centered vertically on desktop */}
                  <div className="relative px-4 pb-8 pt-4 md:py-0 md:px-12 lg:px-16 w-full flex flex-col md:justify-center items-start">
                    {/* Product category tag */}
                    <div className="mb-4 inline-block">
                      <span className={`px-3 py-1 ${product.isDarkTheme ? 'bg-black/10 text-gray-800' : 'bg-white/10 text-purple-200'} backdrop-blur-md rounded-full text-xs tracking-wider uppercase`}>
                        {product.category || 'Featured'}
                      </span>
                    </div>
                    
                    {/* Product name - better contrast on mobile */}
                    <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold ${product.isDarkTheme ? 'text-gray-900 md:text-gray-900' : 'text-white'} mb-4 leading-tight drop-shadow-sm`}>
                      {product.name}
                    </h2>
                    
                    {/* Product description - hidden on mobile, visible on desktop */}
                    <div className="hidden md:block mb-6 relative overflow-hidden">
                      <p className={`text-base ${product.isDarkTheme ? 'text-gray-700' : 'text-slate-200'} leading-relaxed drop-shadow-sm`}>
                        {product.description}
                      </p>
                    </div>
                    
                    {/* Product pricing */}
                    <div className="mb-6">
                      <div className="bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg inline-block">
                        <div className="text-base font-bold">
                          {tProductSlider('price')} {formatPrice(product.lowestPrice)} <span className="inline-block">/&nbsp;{product.isKgPrice ? 'g' : 'unit'}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* CTA button - full width on mobile */}
                    <div className="mt-2 w-full md:w-auto">
                      <ShellButton 
                        href={`/products/${locale !== 'en' && product.translatedSlug ? product.translatedSlug : product.slug}`} 
                        variant={product.isDarkTheme ? "gradient" : "white"} 
                        ariaLabel={tProductSlider('viewProduct')}
                        className="w-full md:w-auto"
                      >
                        {tProductSlider('viewProduct')}
                      </ShellButton>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Product section - hidden on mobile, visible on desktop */}
              <div className="relative hidden md:block md:w-2/3 md:h-full">
                {/* Keeping the background with gradient but commenting out the product image */}
                <div 
                  className="absolute inset-0 md:inset-8 lg:inset-12 xl:inset-16 flex items-center justify-center"
                  style={{
                    background: product.isDarkTheme 
                      ? 'radial-gradient(circle at center, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.15) 30%, rgba(0,0,0,0) 70%)' 
                      : 'radial-gradient(circle at center, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.15) 30%, rgba(255,255,255,0) 70%)'
                  }}
                >
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Slide indicators and navigation buttons - centered on mobile, bottom right on desktop */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:right-8 z-20 flex items-center space-x-4">
        {/* Previous button */}
        <button 
          className={`w-10 h-10 rounded-full ${
            products[currentSlide]?.isDarkTheme 
              ? 'bg-black/10 text-gray-800 hover:bg-black/20' 
              : 'bg-white/10 text-white hover:bg-white/20'
          } backdrop-blur-md flex items-center justify-center transition-colors`}
          onClick={() => handleSlideChange((currentSlide - 1 + products.length) % products.length)}
          aria-label="Previous slide"
          data-theme={products[currentSlide]?.isDarkTheme ? 'dark' : 'light'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        
        {/* Indicators */}
        <div className="flex space-x-4">
          {products.map((product, idx) => (
            <button 
              key={idx} 
              className={`w-10 h-10 p-3 flex items-center justify-center rounded-full transition-all duration-500 ease-in-out ${
                products[currentSlide]?.isDarkTheme ? 'hover:bg-gray-800/20' : 'hover:bg-white/20'
              }`}
              onClick={() => handleSlideChange(idx)}
              data-theme={products[currentSlide]?.isDarkTheme ? 'dark' : 'light'}
              aria-label={`Go to slide ${idx + 1}`}
            >
              <span 
                className="block rounded-full transition-all duration-500 ease-in-out"
                style={{
                  backgroundColor: idx === currentSlide 
                    ? (products[currentSlide]?.isDarkTheme ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 1)')
                    : (products[currentSlide]?.isDarkTheme ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.5)'),
                  width: idx === currentSlide ? '16px' : '8px',
                  height: '8px'
                }}
              />
            </button>
          ))}
        </div>
        
        {/* Next button */}
        <button 
          className={`w-10 h-10 rounded-full ${
            products[currentSlide]?.isDarkTheme 
              ? 'bg-black/10 text-gray-800 hover:bg-black/20' 
              : 'bg-white/10 text-white hover:bg-white/20'
          } backdrop-blur-md flex items-center justify-center transition-colors`}
          onClick={() => handleSlideChange((currentSlide + 1) % products.length)}
          data-theme={products[currentSlide]?.isDarkTheme ? 'dark' : 'light'}
          aria-label="Next slide"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
      
      {/* Client component to handle interactivity */}
      <SliderControls 
        totalSlides={products.length} 
        currentSlide={currentSlide}
        onSlideChange={handleSlideChange}
        autoplay={true}
        autoplayInterval={5000}
      />
    </div>
  );
} 